import React, { useState, useEffect } from "react";
import AuthLayout from "./AuthLayout";
import { resetPassword } from "../../lib/auth-client";

interface ResetPasswordPageProps {
  onNavigate: (page: "login") => void;
  token?: string;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ onNavigate, token }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Get token from URL if not provided
  const resetToken = token || new URLSearchParams(window.location.search).get("token") || "";

  // Calculate password strength
  useEffect(() => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    setPasswordStrength(Math.min(strength, 5));
  }, [password]);

  const getStrengthLabel = () => {
    if (passwordStrength <= 1) return { label: "WEAK", color: "text-red-500" };
    if (passwordStrength <= 2) return { label: "FAIR", color: "text-yellow-500" };
    if (passwordStrength <= 3) return { label: "GOOD", color: "text-yellow-400" };
    if (passwordStrength <= 4) return { label: "STRONG", color: "text-green-400" };
    return { label: "EXCELLENT", color: "text-green-500" };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!password || !confirmPassword) {
      setError("ERROR: ALL FIELDS REQUIRED");
      return;
    }

    if (password !== confirmPassword) {
      setError("ERROR: PASSWORDS DO NOT MATCH");
      return;
    }

    if (password.length < 8) {
      setError("ERROR: PASSWORD MUST BE AT LEAST 8 CHARACTERS");
      return;
    }

    if (!resetToken) {
      setError("ERROR: INVALID OR EXPIRED RESET LINK");
      return;
    }

    setLoading(true);

    try {
      const result = await resetPassword({
        newPassword: password,
        token: resetToken,
      });

      if (result.error) {
        setError(`ERROR: ${result.error.message?.toUpperCase() || "RESET FAILED"}`);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError("ERROR: CONNECTION FAILED. TRY AGAIN.");
    } finally {
      setLoading(false);
    }
  };

  if (!resetToken) {
    return (
      <AuthLayout title="*** ERROR ***">
        <div className="space-y-6 text-center">
          <div className="text-red-500 text-6xl mb-4">✕</div>
          <div className="text-red-400 text-sm">
            INVALID OR EXPIRED RESET LINK
          </div>
          <button
            onClick={() => onNavigate("login")}
            className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm"
          >
            [RETURN TO LOGIN]
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout title="*** PASSWORD RESET ***">
        <div className="space-y-6 text-center">
          <div className="text-green-500 text-6xl mb-4">✓</div>
          <div className="text-green-400 text-sm">
            PASSWORD SUCCESSFULLY RESET
          </div>
          <div className="text-green-600 text-xs">
            YOU CAN NOW LOGIN WITH YOUR NEW PASSWORD.
          </div>
          <button
            onClick={() => onNavigate("login")}
            className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm"
          >
            [LOGIN NOW]
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="*** SET NEW PASSWORD ***">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* New Password */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            NEW PASSWORD:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="********"
              autoFocus
            />
          </div>
          {password && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1 bg-green-900 rounded overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    passwordStrength <= 1 ? "bg-red-500" :
                    passwordStrength <= 2 ? "bg-yellow-500" :
                    passwordStrength <= 3 ? "bg-yellow-400" :
                    "bg-green-500"
                  }`}
                  style={{ width: `${(passwordStrength / 5) * 100}%` }}
                />
              </div>
              <span className={`text-xs ${getStrengthLabel().color}`}>
                {getStrengthLabel().label}
              </span>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            CONFIRM PASSWORD:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError("");
              }}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="********"
            />
          </div>
          {confirmPassword && password !== confirmPassword && (
            <div className="text-red-400 text-xs mt-1">PASSWORDS DO NOT MATCH</div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div
            className="border-2 border-red-500 bg-black p-3 text-center"
            style={{ animation: "shake 0.5s ease-in-out" }}
          >
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.4)" }}
        >
          {loading ? "[RESETTING...]" : "[RESET PASSWORD]"}
        </button>

        {/* Back to login */}
        <button
          type="button"
          onClick={() => onNavigate("login")}
          className="w-full py-2 text-green-600 hover:text-green-400 text-sm"
        >
          [BACK TO LOGIN]
        </button>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
